import React, { useState, useEffect } from "react";
import CustomInput from "../components/common/CustomInput";
import CustomModal from "../components/common/CustomModal";
import CustomButton from "../components/common/CustomButton";
import { COLORS } from "../constants/colors.js";
import { FONTS } from "../constants/fonts.js";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  addContact,
  updateContact,
  deleteContact,
  getContacts,
} from "../utils/contactUtils";
import {
  UserIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  BriefcaseIcon,
} from "@heroicons/react/24/outline";

const styles = {
  safeArea: {
    flex: 1,
    backgroundColor: COLORS.white,
  },
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
    padding: "16px 24px",
    paddingTop: 16,
    minHeight: "100vh",
    maxWidth: 1400,
    margin: "0 auto",
  },
  titleContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 24,
    animation: "fadeIn 0.5s ease-in-out",
  },
  title: {
    fontSize: 32,
    color: COLORS.text,
    fontFamily: FONTS.bold,
    fontWeight: 700,
  },
  searchInput: {
    marginBottom: 16,
  },
  contactItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 20,
    backgroundColor: `${COLORS.secondary}f0`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: 12,
    marginBottom: 16,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    border: `1px solid ${COLORS.tertiary}`,
    transition: "all 0.3s ease",
    animation: "fadeIn 0.5s ease-in-out",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: `0 6px 16px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.2)`,
    },
  },
  contactInfo: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flex: 1,
  },
  contactIcon: {
    width: 28,
    height: 28,
    color: COLORS.primary,
  },
  contactDetails: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  contactName: {
    fontSize: 20,
    color: COLORS.text,
    fontFamily: FONTS.bold,
    fontWeight: 600,
  },
  contactPosition: {
    fontSize: 16,
    color: COLORS.gray,
    fontFamily: FONTS.regularInter,
  },
  contactButtons: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  actionButton: {
    padding: 10,
    borderRadius: 10,
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: COLORS.primary,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  deleteButton: {
    "&:hover": {
      backgroundColor: COLORS.error,
      "& svg": {
        stroke: COLORS.white,
      },
    },
  },
  addButton: {
    position: "fixed",
    bottom: 32,
    right: 32,
    border: "none",
    borderRadius: 32,
    width: 64,
    height: 64,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: COLORS.primary,
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
    cursor: "pointer",
    color: COLORS.white,
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: COLORS.secondary,
      color: COLORS.primary,
      transform: "scale(1.1)",
      boxShadow: `0 6px 20px rgba(${parseInt(
        COLORS.primary.slice(1, 3),
        16
      )}, ${parseInt(COLORS.primary.slice(3, 5), 16)}, ${parseInt(
        COLORS.primary.slice(5, 7),
        16
      )}, 0.3)`,
    },
    "&:active": {
      transform: "scale(0.95)",
    },
  },
  emptyText: {
    fontSize: 18,
    color: COLORS.gray,
    textAlign: "center",
    marginTop: 32,
    fontFamily: FONTS.regularInter,
  },
  loader: {
    fontSize: 18,
    color: COLORS.text,
    textAlign: "center",
    marginVertical: 32,
    fontFamily: FONTS.regularInter,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  loadingSpinner: {
    border: `3px solid ${COLORS.gray}`,
    borderTop: `3px solid ${COLORS.primary}`,
    borderRadius: "50%",
    width: 24,
    height: 24,
    animation: "spin 1s linear infinite",
  },
  button: {
    marginTop: 12,
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(10px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  "@media (max-width: 768px)": {
    container: {
      padding: "12px 16px",
      paddingTop: 16,
    },
    title: {
      fontSize: 28,
    },
    contactItem: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 12,
      padding: 16,
    },
    contactButtons: {
      width: "100%",
      justifyContent: "flex-end",
    },
    addButton: {
      bottom: 16,
      right: 16,
      width: 56,
      height: 56,
    },
  },
};

const AdminContactsManagementScreen = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalVisible, setModalVisible] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const fetchContacts = async () => {
    try {
      const contactList = await getContacts();
      // Sort contacts alphabetically by name
      const sortedContacts = contactList.sort((a, b) =>
        (a.name || "Unnamed").localeCompare(b.name || "Unnamed")
      );
      setContacts(sortedContacts);
    } catch (error) {
      alert("Failed to fetch contacts");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleSaveContact = async () => {
    if (!name || !position) {
      alert("Please enter both contact name and position");
      return;
    }
    setLoading(true);
    try {
      const contactData = { name, position };
      if (editingContact) {
        await updateContact(editingContact.id, contactData);
        alert("Contact updated successfully");
      } else {
        await addContact(contactData);
        alert("Contact added successfully");
      }
      setName("");
      setPosition("");
      setEditingContact(null);
      setModalVisible(false);
      await fetchContacts();
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setName(contact.name);
    setPosition(contact.position || "");
    setModalVisible(true);
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm("Are you sure you want to delete this contact?")) {
      return;
    }
    setDeletingId(id);
    try {
      await deleteContact(id);
      alert("Contact deleted successfully");
      await fetchContacts();
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setDeletingId(null);
    }
  };

  // Filter contacts based on search query
  const filteredContacts = (contacts || []).filter((contact) =>
    (contact.name || "Unnamed")
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const renderContactItem = (item) => (
    <div style={styles.contactItem} key={item.id}>
      <div style={styles.contactInfo}>
        <UserIcon style={styles.contactIcon} />
        <div style={styles.contactDetails}>
          <span style={styles.contactName}>{item.name}</span>
          <span style={styles.contactPosition}>{item.position}</span>
        </div>
      </div>
      <div style={styles.contactButtons}>
        <button
          style={{ ...styles.actionButton }}
          onClick={() => handleEditContact(item)}
        >
          <PencilIcon width={24} height={24} stroke={COLORS.primary} />
        </button>
        <button
          style={{ ...styles.actionButton, ...styles.deleteButton }}
          onClick={() => handleDeleteContact(item.id)}
          disabled={deletingId === item.id}
        >
          {deletingId === item.id ? (
            <div style={styles.loadingSpinner}></div>
          ) : (
            <TrashIcon width={24} height={24} stroke={COLORS.error} />
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div style={styles.safeArea}>
      <div style={styles.container}>
        <div style={styles.titleContainer}>
          <h1 style={styles.title}>Contacts Management</h1>
        </div>
        <CustomInput
          value={searchQuery}
          onChangeText={setSearchQuery}
          icon="search"
          placeholder="Search by contact name"
          style={styles.searchInput}
        />
        {fetching ? (
          <div style={styles.loader}>
            <div style={styles.loadingSpinner}></div>
            <span>Loading...</span>
          </div>
        ) : (
          <div>
            {filteredContacts.length === 0 ? (
              <span style={styles.emptyText}>No contacts available</span>
            ) : (
              filteredContacts.map(renderContactItem)
            )}
          </div>
        )}
        <button
          style={styles.addButton}
          onClick={() => {
            setEditingContact(null);
            setName("");
            setPosition("");
            setModalVisible(true);
          }}
          disabled={loading}
        >
          <PlusIcon width={30} height={30} stroke={COLORS.white} />
        </button>
        <CustomModal
          isVisible={isModalVisible}
          onClose={() => setModalVisible(false)}
          title={editingContact ? "Edit Contact" : "Add Contact"}
          style={{
            backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.gray}`,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
            borderRadius: 12,
            padding: 16,
            maxWidth: 500,
            width: "90%",
            margin: "0 auto",
          }}
        >
          <CustomInput
            value={name}
            onChangeText={setName}
            icon="person"
            placeholder="Enter contact name"
            style={{ marginBottom: 12 }}
          />
          <CustomInput
            value={position}
            onChangeText={setPosition}
            icon="briefcase"
            placeholder="Enter position"
            style={{ marginBottom: 12 }}
          />
          <CustomButton
            title={
              loading
                ? "Saving..."
                : editingContact
                ? "Update Contact"
                : "Create Contact"
            }
            onPress={handleSaveContact}
            style={styles.button}
            disabled={loading}
          />
        </CustomModal>
      </div>
    </div>
  );
};

export default AdminContactsManagementScreen;
